import { WeekDayFlagsHelper } from 'src/app/base/shared/utils/week-day-flags.helper';
import { Duration, WeekdayNumbers } from 'luxon';
import { WeekDayFlags } from 'src/app/base/models/models.swagger.generated';
import { ParkingTariffValidity } from './from-backend/parking-tariff-validity';
import { IDurationRange } from 'src/app/base/shared/time-picker-with-time-slider/time-picker-with-time-slider.component';

export class ParkingTariffValidityHelper {
  public static TWO_DAYS_IN_MILLIS = 48 * 60 * 60 * 1000;
  // used for parking administration -> so max support is start on 23:59 and end on 23:59 next day
  public static findPossibleTariffValidityRangesMatchingForWeekDays(tariffValidities: ParkingTariffValidity[], weekDays: WeekDayFlags): IDurationRange[] {
    const tariffValidityRangeOnDay = new Array<IDurationRange[]>(7);
    tariffValidities.forEach(tariffValidity => {
      let day = tariffValidity.startDateTime.startOf('day');
      let startTime = tariffValidity.startDateTime.diff(day);
      for (let i = 0; i < 7; i++) {
        if (day > tariffValidity.endDateTime) {
          break;
        }
        if (WeekDayFlagsHelper.hasFlags(weekDays, WeekDayFlagsHelper.luxonWeekdayToFlag(day.weekday))) {
          if (!tariffValidityRangeOnDay[day.weekday - 1]) {
            tariffValidityRangeOnDay[day.weekday - 1] = [];
          }
          tariffValidityRangeOnDay[day.weekday - 1].push({
            startTime,
            endTime: tariffValidity.endDateTime.diff(day)
          });
        }
        startTime = Duration.fromObject({hours: 0, minutes: 0});
        day = day.plus({days: 1});
      }
    });
    const tariffValidityRanges = [];
    for (let i = 0; i < 7; i++) {
      if (!WeekDayFlagsHelper.hasFlags(weekDays, WeekDayFlagsHelper.luxonWeekdayToFlag((i + 1) as WeekdayNumbers))) {
        continue;
      }
      if (!tariffValidityRangeOnDay[i]) {
        return [];
      }
      tariffValidityRanges.push(ParkingTariffValidityHelper.mergeValidityRanges(tariffValidityRangeOnDay[i]));
    }
    return ParkingTariffValidityHelper.findIntersectingRanges(tariffValidityRanges);
  }

  public static getCappedEndTime(endTime: Duration): Duration {
    return endTime.toMillis() < ParkingTariffValidityHelper.TWO_DAYS_IN_MILLIS ? endTime : Duration.fromDurationLike({days: 2});
  }

  public static mergeValidityRanges(ranges: IDurationRange[]): IDurationRange[]  {
    ranges.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
    const coveredRangesInDay: IDurationRange[] = [];
    let coveredRange: IDurationRange = {
      startTime: ranges[0].startTime,
      endTime: ParkingTariffValidityHelper.getCappedEndTime(ranges[0].endTime)
    };
    for(let j = 1; j < ranges.length; j++) {
      if (ranges[j].startTime.toMillis() <= coveredRange.endTime.toMillis()) {
        if (ranges[j].endTime.toMillis() > coveredRange.endTime.toMillis()) {
          coveredRange.endTime = ParkingTariffValidityHelper.getCappedEndTime(ranges[j].endTime);
        }
      } else {
        coveredRangesInDay.push(coveredRange);
        coveredRange = {
          startTime: ranges[j].startTime,
          endTime: ParkingTariffValidityHelper.getCappedEndTime(ranges[j].endTime)
        };
      }
    }
    coveredRangesInDay.push(coveredRange);
    return coveredRangesInDay;
  }

  public static findIntersectingRanges(rangesSeparatedByDay: IDurationRange[][]) {
    if (rangesSeparatedByDay?.length == null) {
      return [];
    }
    let actualIntersections = rangesSeparatedByDay[0];
    for(let i = 1; i < rangesSeparatedByDay.length; i++) {
      const newIntersections: IDurationRange[] = [];
      actualIntersections.forEach(intersection => {
        rangesSeparatedByDay[i].forEach(range => {
          if (intersection.startTime >= range.endTime || intersection.endTime <= range.startTime) {
            return;
          }
          newIntersections.push({
            startTime: intersection.startTime > range.startTime ? intersection.startTime : range.startTime,
            endTime: intersection.endTime < range.endTime ? intersection.endTime : range.endTime
          });
        });
      });
      actualIntersections = newIntersections;
    }
    // filter out intersections that actually start at the next day
    const oneDayInMillis = 24 * 60 * 60 * 1000;
    return actualIntersections.filter(i => i.startTime?.toMillis() < oneDayInMillis);
  }
}
